import {Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../i18n/translations';
import {Box, Fab, Grow, List, ListItem, Skeleton, Stack, Typography} from '@mui/material';
import TaskTableItem from './task-table-item';
import {TaskFilterHeader} from './task-filter-header';
import {TaskSortHeader} from './task-sort-header';
import {Checkbox} from '../forms/checkbox';
import {EntityUser} from '../../../backend/responses/entity-user';
import {CareTaskComplementary} from '../../../backend/interfaces/care-task-complementary';
import {EntitySocialResponse} from '../../../backend/interfaces/entity-social-response';
import {CaregiverTaskCompletion, ScreenPaths} from '../../../enums';
import {useFilteredTasks} from '../../../hooks/filtered-tasks';
import {
  DEFAULT_SORT_CONFIGURATION,
  SeniorsSortDrawer,
  SortConfiguration,
} from './seniors-sort-drawer';
import {getTaskStatus} from '../../shared/badges/task-status';
import {EntityActivitiesRepository} from '../../../repositories/entity-activities';
import {CarePlanConfigurationFeature} from '../../../backend/enums/care-plan-configuration-feature';
import {CarePlanConfigurationItem} from '../../../backend/interfaces/care-plan-configuration-item';
import {RolePermissionType} from '../../../backend/enums/role-permission-type';
import {useAuth} from '../../../contexts/auth/context';
import {Add} from '@mui/icons-material';
import {DailyCareLogFiltersDrawer} from '../filters-drawer';

interface SelectProps {
  selectedTasksIds: string[];
  setSelectedTasksIds: Dispatch<SetStateAction<string[]>>;
}

interface TaskTableProps {
  tasks: CareTaskComplementary[] | undefined;
  seniors?: Map<string, EntityUser>;
  entitySocialResponses?: EntitySocialResponse[];
  displayType?: 'user' | 'task';
  hideAvatar?: boolean;
  select?: SelectProps;
  entityShiftId: string;
  date: string;
  defaultPatientId?: string;
}

const taskStatusPriority = (status: CaregiverTaskCompletion): number => {
  switch (status) {
    case CaregiverTaskCompletion.MISSED:
      return 3;
    case CaregiverTaskCompletion.PLANNED:
      return 2;
    case CaregiverTaskCompletion.NOT_DONE:
      return 1;
    case CaregiverTaskCompletion.DONE:
      return 0;
  }
};

export default function TaskTable({
  tasks,
  seniors,
  entitySocialResponses,
  hideAvatar = false,
  displayType = 'task',
  select,
  entityShiftId,
  date,
  defaultPatientId,
}: TaskTableProps) {
  const {t} = useTranslation();
  let {entityId}: {entityId: string} = useParams();
  const history = useHistory();
  const {hasWritePermission} = useAuth();
  const redirectToSeniorShiftTasksDetails = (task: CareTaskComplementary) => {
    const taskUrl = `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Tasks}/${task.careTask.ref}`;
    history.push(taskUrl, {task});
  };
  const [sortDrawerOpen, setSortDrawerOpen] = useState(false);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState<boolean>(false);
  const [sortConfiguration, setSortConfiguration] = useState<SortConfiguration>(
    DEFAULT_SORT_CONFIGURATION
  );
  const [sortedAutonomyAssessmentValues, setSortedAutonomyAssessmentValues] = useState<
    CarePlanConfigurationItem[] | undefined
  >(undefined);

  // Filter the shift care tasks according to the currently active filters
  const {filteredTasks, activeFilters, saveActiveFilters} = useFilteredTasks({tasks, seniors});
  const isSelectableTask = ({careTask}: CareTaskComplementary) => {
    return (Boolean(careTask.feature) || !careTask.markedAsHandled) && !!select;
  };

  const defaultTaskSorting = useCallback(() => {
    const handled =
      filteredTasks
        ?.filter((task) => !task.careTask.markedAsHandled)
        .sort((taskA, taskB) =>
          moment(taskA.careTask.startsAt).isBefore(taskB.careTask.startsAt) ? -1 : 1
        ) || [];
    const notHandled =
      filteredTasks
        ?.filter((task) => task.careTask.markedAsHandled)
        .sort((taskA, taskB) =>
          moment(taskA.careTask.startsAt).isBefore(taskB.careTask.startsAt) ? -1 : 1
        ) || [];

    const sortedAndMerged = [...handled, ...notHandled];

    return filteredTasks ? sortedAndMerged : undefined;
  }, [filteredTasks]);

  const userTaskSorting = useCallback(() => {
    if (!filteredTasks || !seniors || seniors?.size === 0) {
      return;
    }

    return filteredTasks.sort((taskA, taskB) => {
      const seniorA = seniors.get(taskA.careTask.userId)!;
      const seniorB = seniors.get(taskB.careTask.userId)!;

      let ascSorting: number;
      switch (sortConfiguration.value) {
        case 'name':
          ascSorting = seniorA.user.name.localeCompare(seniorB.user.name);
          break;
        case 'autonomy':
          // Check the position of each task's autonomy assessment configuration in the sorted array to sort
          // the tasks.
          if (sortedAutonomyAssessmentValues) {
            const autonomyIndexA = sortedAutonomyAssessmentValues.findIndex(
              (value) => value.id === taskA.autonomyAssessmentConfiguration?.id
            );

            const autonomyIndexB = sortedAutonomyAssessmentValues.findIndex(
              (value) => value.id === taskB.autonomyAssessmentConfiguration?.id
            );

            ascSorting = autonomyIndexB - autonomyIndexA;
          } else {
            // Just a fallback and shouldn't happen, when the user gets to sort the values should be set
            // in the state already.
            ascSorting = 0;
          }
          break;
        case 'status':
          const taskAStatus = getTaskStatus(taskA);
          const taskBStatus = getTaskStatus(taskB);

          ascSorting = taskStatusPriority(taskBStatus) - taskStatusPriority(taskAStatus);
          break;
      }

      return sortConfiguration.direction === 'asc' ? ascSorting : ascSorting * -1;
    });
  }, [seniors, filteredTasks, sortConfiguration, sortedAutonomyAssessmentValues]);

  const sortedTasks = useMemo(() => {
    if (displayType === 'task') {
      return defaultTaskSorting();
    } else {
      return userTaskSorting();
    }
  }, [displayType, defaultTaskSorting, userTaskSorting]);

  const loading = sortedTasks === undefined || seniors === undefined;

  const fetchAutonomyAssessmentValues = useCallback(async () => {
    if (!tasks || tasks.length === 0) {
      return;
    }
    // Any task is ok to grab the sector & group ids
    const sampleTask = tasks[0];

    const groupCarePlanConfigurations =
      await EntityActivitiesRepository.instance.getGroupCarePlanConfigurations(
        entityId,
        sampleTask.careTask.activitiesSectorId,
        sampleTask.careTask.activitiesGroupId
      );
    const autonomyAssessmentFeature = groupCarePlanConfigurations.find(
      (configuration) => configuration.feature === CarePlanConfigurationFeature.AutonomyAssessment
    )!;

    setSortedAutonomyAssessmentValues(autonomyAssessmentFeature?.arrayItems!);
  }, [entityId, tasks]);

  useEffect(() => {
    fetchAutonomyAssessmentValues();
  }, [fetchAutonomyAssessmentValues]);

  return (
    <Stack gap={1}>
      {/*
       *  Drawers
       */}
      <DailyCareLogFiltersDrawer
        activeFilters={activeFilters}
        onFiltersSave={saveActiveFilters}
        open={filtersDrawerOpen}
        onDrawerClose={() => setFiltersDrawerOpen(false)}
      />

      <SeniorsSortDrawer
        sortConfiguration={sortConfiguration}
        onSortConfigurationSave={setSortConfiguration}
        open={sortDrawerOpen}
        onDrawerClose={() => setSortDrawerOpen(false)}
      />

      {/**
       * Headers
       */}
      {!loading &&
        select?.selectedTasksIds &&
        select?.setSelectedTasksIds &&
        tasks?.find((task) => isSelectableTask(task)) && (
          <TaskSortHeader
            tasks={sortedTasks}
            setSelectedTasksIds={select?.setSelectedTasksIds}
            selectedTasksIds={select?.selectedTasksIds}
            onSortClick={() => setSortDrawerOpen(true)}
          />
        )}

      {!select && <TaskFilterHeader onFilterClick={() => setFiltersDrawerOpen(true)} />}

      {/**
       * Tasks
       */}
      <List sx={{paddingBottom: 0, gap: 1, display: 'flex', flexDirection: 'column'}}>
        {!loading && sortedTasks.length === 0 && (
          <h3 className="title">{t(translations.daily_care_log.task_table.empty)}</h3>
        )}

        {loading &&
          new Array(20).fill(1).map((_item, index) => (
            <ListItem
              key={`table_item_skeleton_${index}`}
              sx={{width: '100%', height: '62px', p: 0, m: 0}}
            >
              <Skeleton component="div" animation="wave" sx={{width: '100%', height: '100%'}} />
            </ListItem>
          ))}

        {!loading &&
          sortedTasks.map((taskInfo, index) => {
            const isSelectable = isSelectableTask(taskInfo);

            return (
              <Stack
                key={`task_table_item_${taskInfo.careTask.id}_${index}`}
                flexDirection="row"
                sx={{
                  width: '100%',
                }}
              >
                <Grow in={isSelectable} unmountOnExit>
                  <Box
                    sx={{
                      mr: 1,
                      mt: 2,
                      width: '22px',
                    }}
                  >
                    <Checkbox
                      value={taskInfo.careTask.id}
                      checked={Boolean(select?.selectedTasksIds?.includes(taskInfo?.careTask?.id!))}
                      onChange={(e) => {
                        if (!Boolean(select?.selectedTasksIds?.includes(taskInfo.careTask.id!))) {
                          select?.setSelectedTasksIds((selectedIds) => [
                            ...(selectedIds || []),
                            taskInfo?.careTask?.id!,
                          ]);
                        } else {
                          const filteredSelected = select?.selectedTasksIds?.filter(
                            (id) => id !== e.target.value
                          );
                          if (filteredSelected) {
                            select?.setSelectedTasksIds(filteredSelected);
                          }
                        }
                      }}
                    />
                  </Box>
                </Grow>
                <TaskTableItem
                  entityId={entityId}
                  task={taskInfo}
                  senior={seniors?.get(taskInfo.careTask.userId)}
                  entitySocialResponses={entitySocialResponses}
                  hideAvatar={hideAvatar}
                  displayType={displayType}
                  onClick={() => redirectToSeniorShiftTasksDetails(taskInfo)}
                />
              </Stack>
            );
          })}
      </List>
      {hasWritePermission(RolePermissionType.DailyCareLog) &&
        (hasWritePermission(RolePermissionType.DailyCareLog_BADL) ||
          hasWritePermission(RolePermissionType.DailyCareLog_IADL) ||
          hasWritePermission(RolePermissionType.DailyCareLog_ClinicalSupport) ||
          hasWritePermission(RolePermissionType.DailyCareLog_PersonalDevelopment)) && (
          <Fab
            color="primary"
            className="tasks-floating"
            size="large"
            variant={sortedTasks?.length === 0 ? 'extended' : 'circular'}
            onClick={() => {
              const userQuery = defaultPatientId
                ? `?userId=${encodeURIComponent(defaultPatientId)}`
                : '';
              history.push(
                `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Shift}/${entityShiftId}/new${userQuery}`,
                date
              );
            }}
          >
            <Add />
            {sortedTasks?.length === 0 && (
              <Typography>{t(translations.daily_care_log.occurrences.new)}</Typography>
            )}
          </Fab>
        )}
    </Stack>
  );
}
